// SignupFlow.jsx — iOS signup for Sirat (React component)
// Integrates into App.js. Deep-link invite codes (?code=SIRAT-XXXX from
// WhatsApp shares, or ?claim=1 group-invite links resolved via
// /api/invite/claim) arrive prefilled in the inviteCode prop and trigger
// auto-skip past the welcome + invite screens.
// Safe-area-inset support for Capacitor iOS/Android

import { useState, useEffect } from 'react';

export default function SignupFlow({
  darkMode,
  onComplete, // called when signup succeeds
  // Form state (pass from App.js)
  userName, setUserName,
  userSurname, setUserSurname,
  userWhatsapp, setUserWhatsapp,
  userEmail, setUserEmail,
  inviteCode, setInviteCode,
  validateInviteCode, // async (code) => { valid: bool, reason?: string }
  registering,
  handleRegister,
  handleAppleAuth, // Sign in with Apple handler
  // Permissions state
  showNotifPrompt, setShowNotifPrompt,
  registerPush,
  setNotifEnabled,
  isNative, // true if running in Capacitor (iOS/Android native)
}) {
  const [step, setStep] = useState(0);
  const [emailValid, setEmailValid] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [usedAppleAuth, setUsedAppleAuth] = useState(false);
  const [inviteChecking, setInviteChecking] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // If a code is already present at mount (from App.js's `?code=` URL parsing
  // or the `?claim=1` on-demand fetch — see App.js useEffect on /api/invite/claim),
  // validate it and skip the welcome + invite-code screens. Invalid codes drop
  // the user to step 1 with the bad code and an error visible, so a stale
  // shared link doesn't fail at the end of the flow.
  useEffect(() => {
    if (!inviteCode) return;
    if (!validateInviteCode) {
      setStep(2);
      return;
    }
    (async () => {
      const r = await validateInviteCode(inviteCode);
      if (r?.valid) setStep(2);
      else {
        setStep(1);
        setInviteError(r?.reason === 'already used' ? 'This invite code has already been used.' : 'This invite code is invalid.');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInviteContinue = async () => {
    if (!inviteCode.trim()) return;
    setInviteError('');
    if (!validateInviteCode) {
      setStep(2);
      return;
    }
    setInviteChecking(true);
    try {
      const r = await validateInviteCode(inviteCode);
      if (r?.valid) setStep(2);
      else setInviteError(r?.reason === 'already used' ? 'This invite code has already been used.' : 'This invite code is invalid.');
    } catch {
      setInviteError('Could not verify code. Check your connection and try again.');
    } finally {
      setInviteChecking(false);
    }
  };

  // Validate email
  useEffect(() => {
    if (userEmail) {
      setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail));
    } else {
      setEmailValid(true); // email is optional
    }
  }, [userEmail]);

  const regDigits = userWhatsapp.replace(/[^0-9]/g, '');
  const regValid = regDigits.length === 11 && regDigits.startsWith('27');

  const GOLD = '#F4C56A';
  const GOLD_DIM = 'rgba(244,197,106,0.25)';
  const GOLD_SOFT = 'rgba(244,197,106,0.7)';
  const SERIF = '"Cormorant Garamond", Georgia, serif';
  const TEXT = 'rgba(255,255,255,0.85)';
  const MUTED = 'rgba(255,255,255,0.55)';
  const FAINT = 'rgba(255,255,255,0.35)';

  const screenStyle = {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at 50% 0%, #0F1A17 0%, #060908 55%, #000 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    padding: '60px 24px 24px',
    overflow: 'hidden',
    fontFamily: '-apple-system, system-ui, sans-serif',
  };

  const starfieldStyle = {
    position: 'absolute',
    inset: 0,
    opacity: 0.4,
    pointerEvents: 'none',
    backgroundImage: `
      radial-gradient(1px 1px at 20% 15%, rgba(244,197,106,0.5), transparent),
      radial-gradient(1px 1px at 70% 8%, rgba(255,255,255,0.4), transparent),
      radial-gradient(1px 1px at 85% 25%, rgba(244,197,106,0.3), transparent),
      radial-gradient(1px 1px at 35% 40%, rgba(255,255,255,0.3), transparent)
    `,
  };

  const buttonStyle = {
    width: '100%',
    padding: '17px',
    borderRadius: 16,
    background: 'linear-gradient(180deg, #F4C56A 0%, #D9A347 100%)',
    color: '#1A1208',
    fontSize: 15,
    fontWeight: 700,
    textAlign: 'center',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 8px 30px rgba(244,197,106,0.25), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const inputStyle = {
    width: '100%',
    padding: '15px 16px',
    borderRadius: 14,
    background: 'rgba(20,32,28,0.6)',
    border: '1px solid rgba(244,197,106,0.25)',
    color: '#fff',
    fontSize: 16,
    fontFamily: '-apple-system, system-ui, sans-serif',
    boxSizing: 'border-box',
    marginBottom: 16,
  };

  const StepDots = ({ current }) => (
    <div style={{ display: 'flex', gap: 6 }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: i === current ? 20 : 5,
            height: 5,
            borderRadius: 3,
            background: i <= current ? GOLD : 'rgba(255,255,255,0.15)',
            transition: 'all 300ms',
          }}
        />
      ))}
    </div>
  );

  const BackButton = ({ onClick }) => (
    <button
      onClick={onClick}
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        border: `1px solid ${GOLD_DIM}`,
        background: 'transparent',
        color: GOLD,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M9 2 L4 7 L9 12"
          stroke={GOLD}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const BrandMark = ({ size = 60 }) => (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" />
      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(60 22 22)" />
      <ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.6" opacity="0.85" transform="rotate(120 22 22)" />
    </svg>
  );

  // ──────────────────────────────────────────────────────────────
  // SCREEN 0 — Welcome
  // ──────────────────────────────────────────────────────────────
  if (step === 0) {
    const handleAppleClick = async () => {
      if (handleAppleAuth) {
        try {
          await handleAppleAuth();
        } catch (err) {
          console.error('Apple auth failed:', err);
        }
      }
    };

    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D' }}>
        <div style={starfieldStyle} />
        <div style={screenStyle}>
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              border: `1px solid ${GOLD_DIM}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `inset 0 0 40px rgba(244,197,106,0.1), 0 0 60px rgba(244,197,106,0.15)`,
            }}
          >
            <BrandMark size={62} />
          </div>

          <div style={{ marginTop: 36, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 56, fontWeight: 700, color: '#ffb300', letterSpacing: 4, lineHeight: 1 }}>
            Sirat
          </div>
          <div style={{ marginTop: 14, fontSize: 11, color: MUTED, letterSpacing: 5, textTransform: 'uppercase' }}>
            The Straight Path
          </div>
          <div style={{ marginTop: 20, fontFamily: SERIF, fontSize: 20, color: 'rgba(244,197,106,0.4)', letterSpacing: 2 }}>
            الصراط المستقيم
          </div>

          <div
            style={{
              marginTop: 60,
              paddingLeft: 32,
              paddingRight: 32,
              textAlign: 'center',
              fontFamily: SERIF,
              fontSize: 17,
              fontStyle: 'italic',
              color: TEXT,
              lineHeight: 1.55,
            }}
          >
            "Guide us to the straight path."
          </div>
          <div style={{ marginTop: 10, fontSize: 9, color: GOLD_SOFT, letterSpacing: 2.5, textTransform: 'uppercase' }}>
            Al-Fatihah · 1:6
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={() => setStep(1)}
              style={buttonStyle}
            >
              Begin your journey
            </button>


          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // SCREEN 1 — Invite Code
  // ──────────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D', paddingTop: `calc(14px + env(safe-area-inset-top))` }}>
        <div style={starfieldStyle} />
        <div style={{ ...screenStyle, justifyContent: 'flex-start', paddingTop: 80 }}>
          <div style={{ position: 'absolute', top: 64, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BackButton onClick={() => setStep(0)} />
            <StepDots current={0} />
            <div style={{ width: 36 }} />
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ fontFamily: SERIF, fontSize: 42, fontWeight: 400, color: '#F4F1E8', letterSpacing: -0.5, fontStyle: 'italic', lineHeight: 1.05, marginBottom: 14 }}>
              You're invited.
            </div>
            <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.55, maxWidth: 300, margin: '0 auto' }}>
              Sirat is invite-only. Enter the code from your friend.
            </div>
          </div>

          <div style={{ marginTop: 120, width: 'calc(100% - 48px)', paddingLeft: 24, paddingRight: 24 }}>
            <div style={{ fontSize: 10, color: GOLD_SOFT, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>
              Invite code
            </div>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); if (inviteError) setInviteError(''); }}
              placeholder="SIRAT-XXXX"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{
                ...inputStyle,
                fontFamily: '"SF Mono", monospace',
                letterSpacing: 4,
                textAlign: 'center',
                fontSize: 18,
                borderColor: inviteError ? 'rgba(255,100,100,0.6)' : (inviteCode ? GOLD : 'rgba(244,197,106,0.25)'),
                marginBottom: inviteError ? 8 : 16,
              }}
            />
            {inviteError && (
              <div style={{ fontSize: 12, color: 'rgba(255,140,140,0.95)', textAlign: 'center', lineHeight: 1.4 }}>
                {inviteError}
              </div>
            )}
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={handleInviteContinue}
              disabled={!inviteCode || inviteChecking}
              style={{ ...buttonStyle, opacity: (inviteCode && !inviteChecking) ? 1 : 0.5, cursor: (inviteCode && !inviteChecking) ? 'pointer' : 'default' }}
            >
              {inviteChecking ? 'Checking...' : 'Continue'}
            </button>

            {/* Sign in with Apple — iOS native only. Always visible (not gated
                on inviteCode) so returning users whose data is restored from
                iCloud Keychain can sign back in without a fresh invite. The
                backend's /api/auth/apple matches the Apple `sub` (or links by
                email) and returns the existing user; only brand-new Apple
                accounts get the "needsInvite" branch, which handleAppleAuth
                surfaces as an alert telling them to enter a code first. */}
            {isNative && handleAppleAuth && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 10px' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                  <div style={{ fontSize: 10, color: MUTED, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Already have an account?</div>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                </div>
                <button
                  onClick={() => {
                    setUsedAppleAuth(true);
                    handleAppleAuth?.();
                  }}
                  disabled={registering}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: '#000',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: registering ? 'not-allowed' : 'pointer',
                    opacity: registering ? 0.6 : 1,
                    fontFamily: '-apple-system, system-ui, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}></span>
                  <span>Sign in with Apple</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // SCREEN 2 — Name
  // ──────────────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D', paddingTop: `calc(14px + env(safe-area-inset-top))` }}>
        <div style={starfieldStyle} />
        <div style={{ ...screenStyle, justifyContent: 'flex-start', paddingTop: 80 }}>
          <div style={{ position: 'absolute', top: 64, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BackButton onClick={() => setStep(1)} />
            <StepDots current={1} />
            <div style={{ width: 36 }} />
          </div>

          <div style={{ textAlign: 'center', marginTop: 30, paddingLeft: 28, paddingRight: 28 }}>
            <div style={{ fontSize: 11, color: GOLD_SOFT, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
              Step 1 of 3
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 40, fontWeight: 400, color: '#F4F1E8', lineHeight: 1.05, letterSpacing: -0.5, fontStyle: 'italic' }}>
              What shall we call you?
            </div>
          </div>

          <div style={{ marginTop: 100, width: 'calc(100% - 48px)', paddingLeft: 24, paddingRight: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: GOLD_SOFT, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                First name
              </div>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder=""
                name="firstName"
                autoComplete="given-name"
                autoCapitalize="words"
                autoCorrect="off"
                style={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: GOLD_SOFT, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                Surname
              </div>
              <input
                type="text"
                value={userSurname}
                onChange={(e) => setUserSurname(e.target.value)}
                placeholder=""
                name="surname"
                autoComplete="family-name"
                autoCapitalize="words"
                autoCorrect="off"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={() => setStep(3)}
              disabled={!userName.trim() || !userSurname.trim()}
              style={{ ...buttonStyle, opacity: userName.trim() && userSurname.trim() ? 1 : 0.5, cursor: userName.trim() && userSurname.trim() ? 'pointer' : 'default' }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // SCREEN 3 — Contact (optional)
  // ──────────────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D', paddingTop: `calc(14px + env(safe-area-inset-top))` }}>
        <div style={starfieldStyle} />
        <div style={{ ...screenStyle, justifyContent: 'flex-start', paddingTop: 80 }}>
          <div style={{ position: 'absolute', top: 64, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BackButton onClick={() => setStep(2)} />
            <StepDots current={2} />
            <div style={{ width: 36 }} />
          </div>

          <div style={{ textAlign: 'center', marginTop: 30, paddingLeft: 28, paddingRight: 28 }}>
            <div style={{ fontSize: 11, color: GOLD_SOFT, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
              Step 2 of 3
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 40, fontWeight: 400, color: '#F4F1E8', lineHeight: 1.05, letterSpacing: -0.5, fontStyle: 'italic' }}>
              How do we reach you?
            </div>
          </div>

          <div style={{ marginTop: 100, width: 'calc(100% - 48px)', paddingLeft: 24, paddingRight: 24 }}>
            {/* Phone field */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: GOLD_SOFT, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                Phone number
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '15px 16px',
                  borderRadius: 14,
                  background: 'rgba(20,32,28,0.6)',
                  border: regValid ? `1px solid ${GOLD}` : '1px solid rgba(244,197,106,0.25)',
                  gap: 8,
                }}
              >
                <span style={{ color: regValid ? GOLD : '#fff', fontSize: 14, fontWeight: 600 }}>
                  🇿🇦 +27
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  name="phone"
                  autoComplete="tel-national"
                  value={userWhatsapp.replace(/^27\s?/, '')}
                  onChange={(e) => {
                    let digits = e.target.value.replace(/[^0-9]/g, '');
                    if (!digits) {
                      setUserWhatsapp('');
                      return;
                    }
                    if (digits.startsWith('270')) digits = digits.slice(2);
                    else if (digits.startsWith('27')) digits = digits.slice(2);
                    else if (digits.startsWith('0')) digits = digits.slice(1);
                    digits = digits.slice(0, 9);
                    const p1 = digits.slice(0, 2);
                    const p2 = digits.slice(2, 5);
                    const p3 = digits.slice(5, 9);
                    const local = [p1, p2, p3].filter(Boolean).join(' ');
                    setUserWhatsapp(local ? '27 ' + local : '');
                  }}
                  placeholder="XX XXX XXXX"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    color: regValid ? GOLD : '#fff',
                    fontSize: 16,
                    outline: 'none',
                    fontFamily: 'inherit',
                    letterSpacing: '0.5px',
                  }}
                />
                {regValid && <span style={{ color: GOLD, fontSize: 16 }}>✓</span>}
              </div>
            </div>

            {/* Email field */}
            <div>
              <div style={{ fontSize: 10, color: GOLD_SOFT, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                Email (optional)
              </div>
              <input
                type="email"
                inputMode="email"
                name="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder=""
                style={{
                  ...inputStyle,
                  borderColor: emailValid ? 'rgba(244,197,106,0.25)' : 'rgba(255,100,100,0.5)',
                }}
              />
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={() => setStep(4)}
              disabled={!regValid}
              style={{ ...buttonStyle, opacity: regValid ? 1 : 0.5, cursor: regValid ? 'pointer' : 'default' }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // SCREEN 4 — Add to Home Screen
  // ──────────────────────────────────────────────────────────────
  if (step === 4) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D', paddingTop: `calc(14px + env(safe-area-inset-top))` }}>
        <div style={starfieldStyle} />
        <div style={{ ...screenStyle, justifyContent: 'flex-start', paddingTop: 80 }}>
          <div style={{ position: 'absolute', top: 64, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <BackButton onClick={() => setStep(3)} />
            <StepDots current={3} />
            <div style={{ width: 36 }} />
          </div>

          <div style={{ textAlign: 'center', marginTop: 30, paddingLeft: 28, paddingRight: 28 }}>
            <div style={{ fontSize: 11, color: GOLD_SOFT, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
              Step 3 of 3
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 40, fontWeight: 400, color: '#F4F1E8', lineHeight: 1.05, letterSpacing: -0.5, fontStyle: 'italic' }}>
              Add to Home Screen
            </div>
          </div>

          <div style={{ marginTop: 80, width: 'calc(100% - 48px)', paddingLeft: 24, paddingRight: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: TEXT, lineHeight: 1.6, marginBottom: 28 }}>
              {isIOS
                ? 'Sirat works best when added to your home screen. Tap the Share button, then "Add to Home Screen".'
                : 'Sirat works best when added to your home screen. Tap the menu button, then "Install app" or "Add to Home Screen".'}
            </div>

            <div style={{
              padding: 20,
              borderRadius: 16,
              background: 'rgba(244,197,106,0.06)',
              border: `1px solid ${GOLD_DIM}`,
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>
                {isIOS ? '↗️' : '⋮'}
              </div>
              <div style={{ fontSize: 12, color: GOLD_SOFT, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {isIOS ? 'Share' : 'Menu'}
              </div>
              <div style={{ fontSize: 32, margin: '10px 0', color: GOLD }}>↓</div>
              <div style={{ fontSize: 12, color: GOLD_SOFT, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {isIOS ? 'Add to Home' : 'Install app'}
              </div>
            </div>

            <div style={{ marginTop: 28, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
              This gives you:<br/>
              • Full screen access without the browser bar<br/>
              • Better performance & offline support<br/>
              • Push notifications on lock screen<br/>
              • Full sensor access (location, compass)
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={() => setStep(6)}
              style={buttonStyle}
            >
              Done
            </button>
            <div style={{ marginTop: 12, textAlign: 'center', fontSize: 12, color: MUTED }}>
              You can do this later, but we recommend it now
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // SCREEN 6 — Welcome Home
  // ──────────────────────────────────────────────────────────────
  if (step === 6) {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh', background: '#0A0E0D', paddingTop: `calc(14px + env(safe-area-inset-top))` }}>
        <div style={starfieldStyle} />
        <div style={screenStyle}>
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              border: `1px solid ${GOLD_DIM}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 50px rgba(244,197,106,0.5), inset 0 0 20px rgba(244,197,106,0.1)`,
            }}
          >
            <BrandMark size={54} />
          </div>

          <div style={{ marginTop: 32, fontSize: 10, color: GOLD_SOFT, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
            Assalamu Alaykum
          </div>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: 48,
              fontWeight: 400,
              color: GOLD,
              fontStyle: 'italic',
              lineHeight: 1,
              letterSpacing: -0.5,
              marginBottom: 28,
            }}
          >
            {userName}
          </div>

          <div style={{ fontFamily: SERIF, fontSize: 17, color: GOLD, letterSpacing: 2 }}>
            الصراط المستقيم
          </div>

          <div style={{ position: 'absolute', bottom: 70, left: 24, right: 24, width: 'calc(100% - 48px)' }}>
            <button
              onClick={handleRegister}
              disabled={registering}
              style={{ ...buttonStyle, opacity: registering ? 0.5 : 1, cursor: registering ? 'default' : 'pointer' }}
            >
              {registering ? 'Joining...' : 'Enter Sirat'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
